import React from "react";
import {
  Intent,
  Button,
  Spinner,
  InputGroup,
  Callout,
  Label,
  FormGroup
} from "@blueprintjs/core";
import classnames from "classnames";
import { useHistory } from "react-router-dom";

import { Toolbar, ToolbarGroup } from "../../../../molecules/Toolbar";
import SettingsTab from "../../SettingsTab";
import SettingsForm from "../../SettingsForm";
import FormSection from "../../../../atoms/FormSection/FormSection";
import styles from "../ShippingSettings.module.scss";
import FormInfo from "../../../../atoms/FormInfo/FormInfo";
import SettingsBreadcrumb from "../../SettingsBreadcrumb";
import FormHeading from "../../../../atoms/FormHeading/FormHeading";
import { useShippingZoneDetail, useChangeShippingZone } from "./hooks";
import { SelectList } from "../../../../molecules/SelectList";
import { useStateList } from "../ShippingProfileDetail/hooks";
import DeleteShippingZoneButton from "./DeleteShippingZoneButton";
import ShippingRateList from "./ShippingRateList";

export default function ShippingZoneDetail({
  profileId,
  zoneId,
  selectedStates
}) {
  const history = useHistory();

  const { loading, shippingZone } = useShippingZoneDetail(zoneId);
  const {
    stateIds,
    setStateIds,
    searchText,
    setSearchText,
    zoneName,
    setZoneName,
    onUpdateShippingZone,
    isSubmitDisabled,
    isSaving,
    onToggleRestOfWorld,
    isRestOfWorld,
    error,
    ...shippingRuleProps
  } = useChangeShippingZone({ zoneId, shippingZone });

  const { statesList, loading: stateListLoading } = useStateList(
    searchText,
    true,
    selectedStates ? selectedStates : [],
    shippingZone ? shippingZone.states.map((state) => state.id) : []
  );

  const BREADCRUMBS = [
    {
      text: "Shipping",
      onClick: () => history.push("/settings/shipping")
    },
    {
      text:
        !loading &&
        shippingZone &&
        shippingZone.shippingProfile &&
        shippingZone.shippingProfile.name,
      onClick: () => history.push(`/settings/shipping/${profileId}`)
    },
    {
      text: !loading && shippingZone && shippingZone.name
    }
  ];

  if (loading || !shippingZone) {
    return (
      <SettingsTab centered>
        <Spinner />
      </SettingsTab>
    );
  }

  return (
    <SettingsTab>
      <Toolbar title="Settings">
        <ToolbarGroup>
          <DeleteShippingZoneButton zoneId={zoneId} profileId={profileId} />
          <Button
            text="Save"
            intent={Intent.PRIMARY}
            disabled={isSubmitDisabled}
            loading={isSaving}
            onClick={onUpdateShippingZone}
          />
        </ToolbarGroup>
      </Toolbar>

      <SettingsForm>
        <SettingsBreadcrumb items={BREADCRUMBS} />
        {error && (
          <div className={styles.errorCallout}>
            <Callout
              icon="error"
              intent={Intent.DANGER}
            >
              {error}
            </Callout>
          </div>
        )}
        <FormInfo className={styles.shippingInfo}>
          Nam dapibus nisl vitae elit fringilla rutrum. Aenean sollic itudin,
          erat a elementum rutrum, neque sem pretium metus, quis mollis nisl
          nunc et massa vestibulu.
        </FormInfo>
        <FormSection
          className={classnames(styles.formSectionInput, styles.zoneNameInput)}
        >
          <FormGroup>
            <Label>Zone Name</Label>
            <InputGroup value={zoneName} onChange={setZoneName} />
            <FormInfo className={styles.desc}>
              Customers won't see this.
            </FormInfo>
          </FormGroup>
        </FormSection>
        <FormSection
          className={classnames(
            styles.formSection,
            styles.noBorder,
            styles.zoneRegions
          )}
        >
          <FormHeading>Zone Regions</FormHeading>
          <div className={styles.zoneRegionCheckList}>
            <FormGroup labelFor="zoneSearch" className={styles.searchZoneInput}>
              <InputGroup
                leftIcon="search"
                type="search"
                id="zoneSearch"
                value={searchText}
                onChange={setSearchText}
                placeholder="Search countries and regions"
              />
            </FormGroup>
            <SelectList
              data={statesList}
              value={stateIds}
              onChange={setStateIds}
              loading={stateListLoading}
              emptyListMessage="No regions or countries available"
              secondaryValue={isRestOfWorld}
              secondaryLabel="Rest of World"
              secondaryOnChange={onToggleRestOfWorld}
            />
          </div>
        </FormSection>
        <ShippingRateList
          shippingRules={shippingZone.shippingRules}
          zoneId={zoneId}
          {...shippingRuleProps}
        />
      </SettingsForm>
    </SettingsTab>
  );
}
